import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  ArrowRight, Shield, TrendingUp, Building2, Settings, BarChart3, Zap, Users, FileText,
} from 'lucide-react';

const capabilities = [
  {
    icon: Shield,
    label: 'Financial Control',
    subtitle: 'Operational Discipline',
    title: 'Precision Control at Every Layer',
    desc: 'We design and implement account structures, internal controls, and oversight systems that bring real visibility and discipline to your financial operations. From chart of accounts design to monthly close procedures, we bring enterprise-grade control to your business.',
    points: ['Account structure design', 'Internal controls implementation', 'Monthly close management', 'Financial oversight frameworks'],
  },
  {
    icon: TrendingUp,
    label: 'Strategic Planning',
    subtitle: 'Forward-Looking Vision',
    title: 'Financial Planning Built for Growth',
    desc: 'Beyond historical reporting, we build forward-looking financial models that align your financial trajectory with your business goals. Scenario modeling, budgeting, and KPI tracking help you make confident decisions.',
    points: ['Budget development & management', 'Cash flow forecasting', 'Scenario & sensitivity modeling', 'KPI design & tracking'],
  },
  {
    icon: Building2,
    label: 'Infrastructure',
    subtitle: 'Systems & Architecture',
    title: 'Financial Infrastructure That Scales',
    desc: 'The systems, processes, and organizational structures that are the backbone of sustainable financial operations. Built for scalability, compliance, and long-term operational health.',
    points: ['System setup & optimization', 'Process documentation', 'Financial systems architecture', 'Scalable operational design'],
  },
  {
    icon: Settings,
    label: 'Accounting Ops',
    subtitle: 'Daily Execution',
    title: 'Reliable Operations. Every Single Month.',
    desc: 'Month-end close, bank reconciliations, accounts payable/receivable, and real-time bookkeeping that keeps your financial engine running smoothly. Consistent, reliable, and accurate.',
    points: ['Month-end close process', 'Bank & account reconciliations', 'AP/AR management', 'General ledger maintenance'],
  },
  {
    icon: BarChart3,
    label: 'Reporting',
    subtitle: 'Actionable Intelligence',
    title: 'Reporting That Drives Decisions',
    desc: 'Custom financial packages, dashboards, and reporting frameworks that make your numbers actionable, auditable, and presentable to any stakeholder. We translate data into intelligence.',
    points: ['Custom financial reporting', 'Management reporting packages', 'Investor/lender-ready reports', 'Real-time dashboard design'],
  },
  {
    icon: Zap,
    label: 'Automation',
    subtitle: 'Future-Ready Systems',
    title: 'Intelligent Automation That Delivers',
    desc: 'Workflow automation, intelligent systems, and proprietary tools that reduce manual work, eliminate errors, and accelerate financial operations across your organization.',
    points: ['Workflow automation design', 'Proprietary tool development', 'Process optimization', 'Technology integration'],
  },
  {
    icon: Users,
    label: 'Payroll & Compliance',
    subtitle: 'Complex Compliance',
    title: 'Complex Payroll. Handled with Precision.',
    desc: 'Specialized payroll management for complex, compliance-heavy environments including union contractors, certified payroll, prevailing wage, and federal/state project requirements.',
    points: ['Union contractor payroll', 'Certified payroll reporting', 'Prevailing wage compliance', 'Job-cost payroll allocation'],
    link: '/payroll-compliance',
    linkLabel: 'View Payroll Services',
  },
  {
    icon: FileText,
    label: 'Bankability',
    subtitle: 'Capital Access',
    title: 'Ready for Capital. Ready to Grow.',
    desc: 'Preparing businesses to be fundable, bankable, audit-ready, and positioned to access commercial capital. We help companies achieve the financial credibility lenders and investors require.',
    points: ['Bank & lender package prep', 'Audit readiness assessments', 'Financial credibility building', 'Capital access strategy'],
    link: '/capital-partners',
    linkLabel: 'View Capital Partners',
  },
];

export default function Capabilities() {
  const [activeTab, setActiveTab] = useState(0);
  const cap = capabilities[activeTab];
  const Icon = cap.icon;

  return (
    <div>
      {/* ── Hero ─────────────────────────────────── */}
      <section className="grid-bg pt-32 pb-14" data-testid="capabilities-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <p className="text-[#0ABFBC] text-[10px] font-bold uppercase tracking-[0.25em] mb-4">Our Services</p>
            <h1 className="text-5xl sm:text-6xl font-bold text-[#1A2234] leading-[1.05] tracking-tight mb-5 max-w-2xl">
              Full Financial<br /><span className="text-[#0ABFBC]">Infrastructure Stack</span>
            </h1>
            <p className="text-lg text-[#475569] max-w-xl leading-relaxed">
              Eight integrated capabilities covering every layer of financial control — from daily operations to strategic planning and intelligent automation.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ── Tab Section ──────────────────────────── */}
      <section className="bg-white pb-20" data-testid="capabilities-tabs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Tab Bar */}
          <div className="border-b border-slate-100">
            <div className="flex overflow-x-auto no-scrollbar">
              {capabilities.map((c, i) => (
                <button
                  key={c.label}
                  onClick={() => setActiveTab(i)}
                  data-testid={`cap-tab-${i}`}
                  className={`px-5 py-4 text-sm font-semibold whitespace-nowrap border-b-2 transition-colors duration-200 ${
                    i === activeTab
                      ? 'text-[#0ABFBC] border-[#0ABFBC]'
                      : 'text-[#475569] border-transparent hover:text-[#1A2234]'
                  }`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.28, ease: 'easeOut' }}
              className="py-14 grid grid-cols-1 lg:grid-cols-5 gap-12 items-center"
            >
              {/* Left */}
              <div className="lg:col-span-3">
                <div className="w-12 h-12 rounded-xl bg-[#0ABFBC]/10 flex items-center justify-center mb-5">
                  <Icon size={22} strokeWidth={1.5} className="text-[#0ABFBC]" />
                </div>
                <p className="text-[#0ABFBC] text-[10px] font-bold uppercase tracking-[0.2em] mb-2">{cap.subtitle}</p>
                <h3 className="text-3xl sm:text-4xl font-bold text-[#1A2234] mb-4 leading-tight">{cap.title}</h3>
                <p className="text-[#475569] leading-relaxed mb-7">{cap.desc}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 mb-9">
                  {cap.points.map(p => (
                    <div key={p} className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#0ABFBC] flex-shrink-0 mt-[7px]" />
                      <span className="text-[#475569] text-sm">{p}</span>
                    </div>
                  ))}
                </div>
                <Link
                  to={cap.link || '/contact'}
                  data-testid={`cap-panel-cta-${activeTab}`}
                  className="inline-flex items-center gap-1.5 text-[#0ABFBC] font-bold text-xs uppercase tracking-wider hover:gap-3 transition-all"
                >
                  {cap.linkLabel || 'Schedule a Consultation'} <ArrowRight size={13} />
                </Link>
              </div>

              {/* Right — visual */}
              <div className="lg:col-span-2 flex justify-center">
                <div className="w-56 h-56 rounded-full bg-[#0ABFBC]/5 flex items-center justify-center">
                  <div className="w-40 h-40 rounded-full bg-[#0ABFBC]/8 flex items-center justify-center">
                    <div className="w-24 h-24 rounded-full bg-[#0ABFBC]/12 flex items-center justify-center">
                      <Icon size={44} strokeWidth={0.9} className="text-[#0ABFBC]/60" />
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      {/* ── Dark CTA ──────────────────────────────── */}
      <section className="dark-grid-bg py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}>
            <h2 className="text-4xl font-bold text-white mb-4">Ready to Build Something Stronger?</h2>
            <p className="text-slate-400 text-lg mb-8 max-w-xl mx-auto">
              Tell us about your business and we'll identify the right capabilities for your stage.
            </p>
            <Link to="/contact" data-testid="capabilities-cta" className="bg-[#0ABFBC] text-white px-8 py-3.5 rounded-md font-semibold hover:bg-[#09A09D] transition-colors inline-flex items-center gap-2">
              Schedule a Consultation <ArrowRight size={17} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
